import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, CreditCard, Download, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  getPayables,
  createPayable,
  updatePayable,
  deletePayable,
  recordPayablePayment,
  resetPayablePayment,
  getBankAccounts,
  getStatutoryMonthly,
  formatCurrency,
  formatDate,
  handleNumericInput,
  parseNumericValue,
  formatIndianNumber,
} from "../lib/api";
import { COMPANIES, COMPANY_OPTIONS, getCompanyLabel, COMPANY_COLORS } from "../lib/companies";
import { toast } from "sonner";
import ExportDialog from "../components/ExportDialog";
import GSTInput from "../components/GSTInput";
import { DatePicker } from "../components/DatePicker";
import { ProjectAllocation } from "../components/ProjectAllocation";

const emptyPayable = {
  company: "empact-studio",
  vendor_name: "",
  description: "",
  base_amount: "",
  cgst_rate: "",
  sgst_rate: "",
  igst_rate: "",
  tds_rate: "",
  status: "pending",
  due_date: "",
};

const StatusBadge = ({ status }) => {
  const statusClasses = {
    pending: "bg-amber-100 text-amber-700",
    partial: "bg-blue-100 text-blue-700",
    paid: "bg-emerald-100 text-emerald-700",
  };
  const labels = {
    pending: "Pending",
    partial: "Partial",
    paid: "Paid",
  };
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || statusClasses.pending}`}>
      {labels[status] || status}
    </span>
  );
};

export default function Payables() {
  const { canEdit } = useAuth();
  const [payables, setPayables] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [formData, setFormData] = useState(emptyPayable);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [selectedBankAccounts, setSelectedBankAccounts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [payDialog, setPayDialog] = useState(null); // payable being paid via dialog
  const [payDialogAllocations, setPayDialogAllocations] = useState([{ project_id: "", line_item_id: "", amount: "", percent: "" }]);
  const [payDate, setPayDate] = useState("");
  const [tdsPaidMonths, setTdsPaidMonths] = useState(new Set());

  const fetchPayables = useCallback(async () => {
    try {
      const [payablesRes, bankRes, statRes] = await Promise.all([
        getPayables(),
        getBankAccounts(),
        getStatutoryMonthly("all"),
      ]);
      setPayables(payablesRes.data);
      setBankAccounts(bankRes.data);
      const paidSet = new Set();
      (statRes.data?.tds || []).forEach(t => { if (t.status === "paid") paidSet.add(t.month); });
      setTdsPaidMonths(paidSet);
    } catch (error) {
      toast.error("Failed to load payables");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayables();
  }, [fetchPayables]);

  const handleOpenDialog = (payable = null) => {
    if (payable) {
      setSelectedPayable(payable);
      setFormData({
        company: payable.company || "empact-studio",
        vendor_name: payable.vendor_name,
        description: payable.description,
        base_amount: payable.base_amount,
        cgst_rate: payable.cgst_rate,
        sgst_rate: payable.sgst_rate,
        igst_rate: payable.igst_rate,
        tds_rate: payable.tds_rate?.toString() || "0",
        status: payable.status,
        due_date: payable.due_date,
      });
    } else {
      setSelectedPayable(null);
      setFormData({...emptyPayable, company: filterCompany !== "all" ? filterCompany : "empact-studio"});
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPayable(null);
    setFormData(emptyPayable);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert empty string amounts to numbers for submission
      const submitData = {
        ...formData,
        base_amount: parseNumericValue(formData.base_amount),
        cgst_rate: parseNumericValue(formData.cgst_rate),
        sgst_rate: parseNumericValue(formData.sgst_rate),
        igst_rate: parseNumericValue(formData.igst_rate),
        tds_rate: parseNumericValue(formData.tds_rate),
      };
      
      if (selectedPayable) {
        await updatePayable(selectedPayable.id, submitData);
        toast.success("Payable updated successfully");
      } else {
        await createPayable(submitData);
        toast.success("Payable added successfully");
      }
      handleCloseDialog();
      fetchPayables();
    } catch (error) {
      toast.error("Failed to save payable");
    }
  };

  const handleDelete = async () => {
    try {
      await deletePayable(selectedPayable.id);
      toast.success("Payable deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedPayable(null);
      fetchPayables();
    } catch (error) {
      toast.error("Failed to delete payable");
    }
  };

  // Handle payment amount change
  const handlePaymentAmountChange = (payableId, value) => {
    setPaymentAmounts(prev => ({
      ...prev,
      [payableId]: handleNumericInput(value)
    }));
  };

  // Handle bank account selection for payment
  const handleBankAccountChange = (payableId, bankAccountId) => {
    setSelectedBankAccounts(prev => ({
      ...prev,
      [payableId]: bankAccountId
    }));
  };

  // Record a payment
  const handleRecordPayment = async (payable) => {
    const amountStr = paymentAmounts[payable.id];
    const amount = parseNumericValue(amountStr);
    const bankAccountId = selectedBankAccounts[payable.id];
    
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    
    if (!bankAccountId) {
      toast.error("Please select a bank account");
      return;
    }
    
    // Open payment dialog to optionally assign to project
    setPayDialog(payable);
    setPayDialogAllocations([{ project_id: "", line_item_id: "", amount: "", percent: "" }]);
  };

  // Confirm payment (from dialog)
  const handleConfirmPayment = async () => {
    const payable = payDialog;
    const amountStr = paymentAmounts[payable.id];
    const amount = parseNumericValue(amountStr);
    const bankAccountId = selectedBankAccounts[payable.id];
    
    const validAllocs = payDialogAllocations.filter(a => a.project_id && a.line_item_id && parseFloat(a.amount) > 0);
    const projectAllocPayload = validAllocs.length > 0
      ? { project_allocations: validAllocs.map(a => ({ project_id: a.project_id, line_item_id: a.line_item_id, amount: parseFloat(a.amount) })) }
      : null;

    try {
      setSavingId(payable.id);
      await recordPayablePayment(payable.id, amount, bankAccountId, projectAllocPayload, payDate);
      toast.success(`Payment of ${formatCurrency(amount)} recorded`);
      setPaymentAmounts(prev => {
        const newState = { ...prev };
        delete newState[payable.id];
        return newState;
      });
      setSelectedBankAccounts(prev => {
        const newState = { ...prev };
        delete newState[payable.id];
        return newState;
      });
      setPayDialog(null);
      setPayDate("");
      fetchPayables();
    } catch (error) {
      toast.error("Failed to record payment");
    } finally {
      setSavingId(null);
    }
  };

  // Reset payment (undo)
  const handleResetPayment = async (payable) => {
    try {
      setSavingId(payable.id);
      await resetPayablePayment(payable.id);
      toast.success("Payment reset to pending (bank balance restored)");
      fetchPayables();
    } catch (error) {
      toast.error("Failed to reset payment");
    } finally {
      setSavingId(null);
    }
  };

  const filteredPayables = payables.filter((pay) => {
    const statusMatch = filterStatus === "all" || pay.status === filterStatus;
    const companyMatch = filterCompany === "all" || pay.company === filterCompany;
    return statusMatch && companyMatch;
  });

  const totalNetPayable = filteredPayables.reduce((sum, pay) => sum + (pay.net_payable || pay.total_amount), 0);
  const totalPaidSoFar = filteredPayables.reduce((sum, pay) => sum + (pay.amount_paid || 0), 0);
  const totalBalance = filteredPayables.reduce((sum, pay) => sum + (pay.balance || (pay.net_payable || pay.total_amount) - (pay.amount_paid || 0)), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payables-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Payables</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track outgoing payments with GST details
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setExportDialogOpen(true)}
            data-testid="export-payables"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          {canEdit && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              data-testid="add-payable-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Payable
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Net Payable</p>
                  <p className="text-sm sm:text-xl md:text-2xl font-bold text-slate-900 tabular-nums">
                    {formatCurrency(totalNetPayable)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Paid So Far</p>
                  <p className="text-sm sm:text-xl md:text-2xl font-bold text-emerald-600 tabular-nums">
                    {formatCurrency(totalPaidSoFar)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Balance Pending</p>
                  <p className="text-sm sm:text-xl md:text-2xl font-bold text-amber-600 tabular-nums">
                    {formatCurrency(totalBalance)}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={filterCompany} onValueChange={setFilterCompany}>
                  <SelectTrigger className="w-40" data-testid="filter-payable-company">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANIES.map((company) => (
                      <SelectItem key={company.value} value={company.value}>
                        {company.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-32" data-testid="filter-payable-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Company Breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100">
              {COMPANY_OPTIONS.map((company) => {
                const companyBalance = payables
                  .filter((pay) => pay.company === company.value && pay.status !== "paid")
                  .reduce((sum, pay) => sum + (pay.balance || (pay.net_payable || pay.total_amount) - (pay.amount_paid || 0)), 0);
                return (
                  <div key={company.value} className={`p-3 rounded-lg ${COMPANY_COLORS[company.value]}`}>
                    <p className="text-xs font-medium opacity-80">{company.label}</p>
                    <p className="text-sm sm:text-lg font-bold tabular-nums">{formatCurrency(companyBalance)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filteredPayables.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Base Amount</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                  <TableHead className="text-right">Net Payable</TableHead>
                  <TableHead className="text-right w-40">Pay Amount</TableHead>
                  <TableHead className="text-right">Paid So Far</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center w-32">Action</TableHead>
                  <TableHead className="text-right">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayables.map((payable) => {
                  const baseAmount = payable.base_amount || 0;
                  const gstAmount = (payable.cgst_amount || 0) + (payable.sgst_amount || 0) + (payable.igst_amount || 0);
                  const tdsAmount = payable.tds_amount || 0;
                  const netPayable = payable.net_payable || (payable.total_amount - tdsAmount);
                  const paidSoFar = payable.amount_paid || 0;
                  const balance = payable.balance !== undefined ? payable.balance : (netPayable - paidSoFar);
                  const isFullyPaid = balance <= 0;
                  const paymentAmount = paymentAmounts[payable.id] || "";
                  
                  return (
                  <TableRow 
                    key={payable.id} 
                    data-testid={`payable-row-${payable.id}`}
                    className={isFullyPaid ? "bg-emerald-50/50" : payable.status === "partial" ? "bg-blue-50/50" : ""}
                  >
                    <TableCell>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${COMPANY_COLORS[payable.company] || COMPANY_COLORS.all}`}>
                        {getCompanyLabel(payable.company)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{payable.vendor_name}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[150px]">
                          {payable.description}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-700">
                      {formatCurrency(baseAmount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-blue-600">
                      {gstAmount > 0 ? (
                        <>
                          +{formatCurrency(gstAmount)}
                          <span className="text-xs text-blue-400 block">
                            {payable.cgst_amount > 0 && `C:${payable.cgst_rate}%`}
                            {payable.sgst_amount > 0 && ` S:${payable.sgst_rate}%`}
                            {payable.igst_amount > 0 && ` I:${payable.igst_rate}%`}
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-600">
                      {(() => {
                        const month = payable.created_at ? payable.created_at.slice(0, 7) : "";
                        const isTdsPaid = tdsPaidMonths.has(month);
                        return tdsAmount > 0 ? (
                          <div className={isTdsPaid ? "line-through text-red-500" : ""}>
                            -{formatCurrency(tdsAmount)}
                            <span className={`text-xs block ${isTdsPaid ? "text-red-400" : "text-emerald-400"}`}>
                              @{payable.tds_rate}% {isTdsPaid && "· Paid"}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-brand-navy">
                      {formatCurrency(netPayable)}
                    </TableCell>
                    <TableCell className="text-right">
                      {!isFullyPaid ? (
                        <div className="flex flex-col gap-1">
                          <div className="relative inline-flex">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={paymentAmount}
                              onChange={(e) => handlePaymentAmountChange(payable.id, e.target.value)}
                              onBlur={(e) => {
                                const val = parseNumericValue(e.target.value);
                                if (val > 0) {
                                  setPaymentAmounts(prev => ({...prev, [payable.id]: formatIndianNumber(val)}));
                                }
                              }}
                              className="text-right pr-3 pl-7 w-36 font-medium"
                              placeholder={formatIndianNumber(balance)}
                              data-testid={`payment-input-${payable.id}`}
                            />
                          </div>
                          <Select
                            value={selectedBankAccounts[payable.id] || ""}
                            onValueChange={(value) => handleBankAccountChange(payable.id, value)}
                          >
                            <SelectTrigger className="w-36 h-8 text-xs" data-testid={`bank-select-${payable.id}`}>
                              <SelectValue placeholder="From Bank..." />
                            </SelectTrigger>
                            <SelectContent>
                              {bankAccounts.map((bank) => (
                                <SelectItem key={bank.id} value={bank.id} className="text-xs">
                                  {bank.name} ({formatCurrency(bank.balance)})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={paidSoFar > 0 ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                        {formatCurrency(paidSoFar)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={balance > 0 ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                        {formatCurrency(balance)}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(payable.due_date)}</TableCell>
                    <TableCell>
                      <StatusBadge status={payable.status} />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col gap-1 items-center">
                        {!isFullyPaid && (
                          <Button
                            onClick={() => handleRecordPayment(payable)}
                            disabled={savingId === payable.id || !paymentAmount}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3 w-full"
                            data-testid={`pay-btn-${payable.id}`}
                          >
                            {savingId === payable.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              "Pay"
                            )}
                          </Button>
                        )}
                        {paidSoFar > 0 && (
                          <Button
                            onClick={() => handleResetPayment(payable)}
                            disabled={savingId === payable.id}
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 text-xs w-full"
                            data-testid={`reset-btn-${payable.id}`}
                          >
                            {savingId === payable.id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              "Undo"
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(payable)}
                          data-testid={`edit-payable-${payable.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          onClick={() => {
                            setSelectedPayable(payable);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-payable-${payable.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="empty-state">
              <CreditCard className="empty-state-icon" />
              <h3 className="empty-state-title">No payables yet</h3>
              <p className="empty-state-text">Add your first payable to track outgoing payments</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Payable
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="payable-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedPayable ? "Edit Payable" : "Add Payable"}
            </DialogTitle>
            <DialogDescription>
              {selectedPayable ? "Update the payable details below." : "Fill in the details to add a new payable."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company">Company</Label>
              <Select
                value={formData.company}
                onValueChange={(value) => setFormData({ ...formData, company: value })}
              >
                <SelectTrigger data-testid="select-payable-company">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_OPTIONS.map((company) => (
                    <SelectItem key={company.value} value={company.value}>
                      {company.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="vendor_name">Vendor Name</Label>
              <Input
                id="vendor_name"
                value={formData.vendor_name}
                onChange={(e) =>
                  setFormData({ ...formData, vendor_name: e.target.value })
                }
                placeholder="Vendor/Supplier name"
                required
                data-testid="input-vendor-name"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Service/Product description"
                data-testid="input-payable-description"
              />
            </div>

            <div>
              <Label htmlFor="base_amount">Base Amount (₹)</Label>
              <Input
                id="base_amount"
                type="text"
                inputMode="decimal"
                value={formData.base_amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    base_amount: handleNumericInput(e.target.value),
                  })
                }
                placeholder="Enter amount"
                required
                data-testid="input-payable-base-amount"
              />
            </div>

            <GSTInput
              cgst_rate={formData.cgst_rate}
              sgst_rate={formData.sgst_rate}
              igst_rate={formData.igst_rate}
              handleNumericInput={handleNumericInput}
              onChange={(vals) => setFormData((prev) => ({ ...prev, ...vals }))}
            />

            <div>
              <Label htmlFor="tds_rate" className="text-red-600">TDS Rate (%)</Label>
              <Select
                value={formData.tds_rate?.toString() || "0"}
                onValueChange={(value) => setFormData({ ...formData, tds_rate: value })}
              >
                <SelectTrigger data-testid="input-payable-tds">
                  <SelectValue placeholder="Select TDS Rate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No TDS</SelectItem>
                  <SelectItem value="1">1% - Insurance Commission</SelectItem>
                  <SelectItem value="2">2% - Contractors/Professionals</SelectItem>
                  <SelectItem value="5">5% - Rent (below threshold)</SelectItem>
                  <SelectItem value="10">10% - Professional Fees/Rent</SelectItem>
                  <SelectItem value="20">20% - No PAN</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Live Calculation Preview */}
            {parseNumericValue(formData.base_amount) > 0 && (() => {
              const base = parseNumericValue(formData.base_amount);
              const cgst = Math.round(base * parseNumericValue(formData.cgst_rate) / 100 * 100) / 100;
              const sgst = Math.round(base * parseNumericValue(formData.sgst_rate) / 100 * 100) / 100;
              const igst = Math.round(base * parseNumericValue(formData.igst_rate) / 100 * 100) / 100;
              const gstTotal = cgst + sgst + igst;
              const total = base + gstTotal;
              const tds = Math.round(base * parseNumericValue(formData.tds_rate) / 100 * 100) / 100;
              const net = total - tds;
              return (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5 text-sm" data-testid="payable-calc-preview">
                  <div className="flex justify-between"><span className="text-slate-500">Base Amount</span><span className="font-medium tabular-nums">{formatCurrency(base)}</span></div>
                  {gstTotal > 0 && <div className="flex justify-between text-blue-600"><span>+ GST ({cgst > 0 ? `CGST ${formatCurrency(cgst)} + SGST ${formatCurrency(sgst)}` : `IGST ${formatCurrency(igst)}`})</span><span className="font-medium tabular-nums">{formatCurrency(gstTotal)}</span></div>}
                  <div className="flex justify-between border-t pt-1.5"><span className="text-slate-500">Total Amount</span><span className="font-bold tabular-nums">{formatCurrency(total)}</span></div>
                  {tds > 0 && <div className="flex justify-between text-red-600"><span>- TDS @ {parseNumericValue(formData.tds_rate)}%</span><span className="font-medium tabular-nums">-{formatCurrency(tds)}</span></div>}
                  <div className="flex justify-between border-t pt-1.5 text-brand-navy"><span className="font-semibold">Net Payable</span><span className="font-bold text-base tabular-nums">{formatCurrency(net)}</span></div>
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <DatePicker
                  value={formData.due_date}
                  onChange={(val) => setFormData({ ...formData, due_date: val })}
                  data-testid="input-payable-due-date"
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger data-testid="select-payable-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
                data-testid="save-payable"
              >
                {selectedPayable ? "Update" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payable</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this payable to "{selectedPayable?.vendor_name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-payable"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}

      {/* Payment Project Allocation Dialog */}
      <Dialog open={!!payDialog} onOpenChange={(open) => { if (!open) setPayDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="pay-project-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
          </DialogHeader>
          {payDialog && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Vendor</span><span className="font-medium">{payDialog.vendor_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-bold text-brand-navy">{formatCurrency(parseNumericValue(paymentAmounts[payDialog.id]))}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">From</span><span className="font-medium">{bankAccounts.find(b => b.id === selectedBankAccounts[payDialog.id])?.name || '-'}</span></div>
              </div>

              <ProjectAllocation
                allocations={payDialogAllocations}
                totalAmount={parseNumericValue(paymentAmounts[payDialog.id])}
                onChange={setPayDialogAllocations}
              />

              <div>
                <Label className="text-sm">Payment Date</Label>
                <DatePicker value={payDate} onChange={(v) => setPayDate(v)} data-testid="pay-date-picker" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setPayDialog(null); setPayDate(""); }}>Cancel</Button>
                <Button onClick={handleConfirmPayment} disabled={savingId === payDialog.id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-pay-btn">
                  {savingId === payDialog.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Confirm Payment"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        dataType="payables"
        title="Payables"
        showDateFilter={true}
        showCompanyFilter={true}
      />
    </div>
  );
}
